import {
  createContext,
  useState
} from 'react'
import {
  erase,
  get,
  set
} from 'browser-cookies'
import type { ReactNode } from 'react'

type SpaceIdType = string
type Role = 'dai:admin' | 'dai:user' | 'dai:viewer'

interface Context {
  selectedSpaceId?: SpaceIdType
  setSelectedSpaceId: (newSpaceId?: SpaceIdType) => void
  role?: Role
  setRole: (newRole?: Role) => void
  selectedSpaceName?: string
  setSelectedSpaceName: (value: string) => void
}

export const SpaceContext = createContext<Context | undefined>(undefined)

const COOKIE_NAME = 'selectedSpaceId'

interface ProviderProps {
  children: ReactNode
  selectedSpaceId?: SpaceIdType // can be used to change the cookie
}

export const SpaceProvider = ({
  children,
  selectedSpaceId: spaceIdProp
}: ProviderProps) => {
  const cookie = get(COOKIE_NAME) || undefined

  const [selectedSpaceId, setSelectedSpaceIdState] = useState<SpaceIdType | undefined>(spaceIdProp || cookie)
  const [selectedSpaceName, setSelectedSpaceName] = useState<string | undefined>('Space')
  const [role, setRole] = useState<Role | undefined>(undefined)

  const updateCookie = (newSpaceId?: SpaceIdType) => {
    if (newSpaceId) {
      set(COOKIE_NAME, newSpaceId)
    } else {
      erase(COOKIE_NAME)
    }
  }

  if (selectedSpaceId !== cookie) updateCookie(selectedSpaceId)

  const setSelectedSpaceId = (newSpaceId?: SpaceIdType) => {
    updateCookie(newSpaceId)
    setSelectedSpaceIdState(newSpaceId)
  }

  const value = {
    selectedSpaceId,
    setSelectedSpaceId,
    selectedSpaceName,
    setSelectedSpaceName,
    role,
    setRole
  }

  return (
    <SpaceContext.Provider value={value}>
      {children}
    </SpaceContext.Provider>
  )
}

interface ConsumerProps {
  children: (context: Context) => ReactNode
}

export const SpaceConsumer = ({
  children: renderChildren
}: ConsumerProps) =>
  <SpaceContext.Consumer>
    {
      (context?: Context) => {
        if (context === undefined) {
          throw new Error('<SpaceConsumer /> must be used within <SpaceProvider />')
        }

        return renderChildren(context)
      }
    }
  </SpaceContext.Consumer>
